import { ArcRotateCamera, Camera, FreeCamera, Gamepad, GenericPad, Vector3, Xbox360Pad } from '@babylonjs/core';
import { Level } from './Level';

export class CameraController {
  camera: ArcRotateCamera;
  freeCamera: FreeCamera;

  canvas: HTMLCanvasElement;

  private readonly CAMERA_STICK_SENSITIVITY = 2.5; // Radians per second at full stick deflection
  private readonly DEFAULT_BETA = Math.PI / 3;
  private readonly FREE_CAMERA_GAMEPAD_SPEED = 100; // Movement speed for gamepad
  private readonly FREE_CAMERA_ROTATION_SPEED = 5; // Rotation speed for free camera gamepad

  private targetPosition: Vector3 = Vector3.Zero();
  private targetAlpha: number = -Math.PI / 2;
  /**
   * Align camera rotation to follow the path direction
   */
  private lastNearestIndex: number = 0;

  // Gamepad for free camera
  private freeCameraGamepad: Gamepad | null = null;

  constructor(private level: Level) {
    // Create ArcRotateCamera behind player (Crash Bandicoot style)
    // Level is built along Z-axis: platforms at Z=10, 18, 26...
    // Player moves in +Z direction, camera should be behind (at -Z side)
    // ArcRotateCamera position formula:
    // x = target.x + radius * sin(beta) * cos(alpha)
    // z = target.z + radius * sin(beta) * sin(alpha)
    //
    // For camera at -Z: sin(alpha) = -1, cos(alpha) = 0 → Alpha = -PI/2

    this.canvas = this.level.game.canvas;

    if (this.level.game.config.sceneViewer) {
      this.freeCamera = new FreeCamera('freeCamera', new Vector3(0, 50, 0), this.level.game.scene);
      this.level.scene.activeCamera = this.freeCamera;
      this.freeCamera.attachControl(this.canvas, true);

      // Configure WASD keys for free camera movement
      this.freeCamera.keysUp = [87]; // W
      this.freeCamera.keysDown = [83]; // S
      this.freeCamera.keysLeft = [65]; // A
      this.freeCamera.keysRight = [68]; // D
      this.freeCamera.keysUpward = [69]; // E (up)
      this.freeCamera.keysDownward = [81]; // Q (down)

      // Setup gamepad for free camera
      this.setupFreeCameraGamepad();
      return;
    }

    const startIndex = this.level.config.player.startPathIndex;
    const initialSettings = this.lookAtPathIndex(startIndex);
    const initialAlpha = initialSettings.alpha;
    const cameraPos = initialSettings.position;

    this.camera = new ArcRotateCamera(
      'followCamera',
      initialAlpha, // Alpha: aligned to path or default -Z axis
      this.DEFAULT_BETA, // Beta: 45 degrees down for better view
      this.level.config.camera.distance,
      cameraPos,
      this.level.scene
    );

    if (this.level.config.camera.collision) {
      this.camera.checkCollisions = true;
      this.camera.collisionRadius = new Vector3(2, 2, 2); // Camera collision sphere radius
    }

    // Camera limits
    this.camera.lowerRadiusLimit = this.level.config.camera.distance;
    this.camera.upperRadiusLimit = this.level.config.camera.distance;
    this.camera.lowerBetaLimit = 0.1;
    this.camera.upperBetaLimit = Math.PI - 0.1;

    // Lock beta axis to horizontal
    //this.camera.lowerBetaLimit = Math.PI / 2;
    //this.camera.upperBetaLimit = Math.PI / 2;

    // Configure orthographic mode if enabled
    if (this.level.config.camera.orthographic) {
      this.setupOrthographicCamera();
    }

    // Attach controls for optional orbit but disable keyboard input
    // Keyboard input should go to InputManager, not the camera
    this.camera.attachControl(this.canvas, true);
    this.camera.inputs.removeByType('ArcRotateCameraKeyboardMoveInput');

    // Disable panning
    this.camera.panningSensibility = 0;

    // Make it the active camera
    this.level.scene.activeCamera = this.camera;

    this.level.game.audioEngine.listener.attach(this.level.scene.activeCamera);
  }

  update(deltaTime: number) {
    // Handle free camera gamepad input in sceneViewer mode
    if (this.level.game.config.sceneViewer) {
      this.updateFreeCamera(deltaTime);
      return;
    }

    if (!this.level.player || !this.camera) {
      return;
    }
    // Smoothly follow player
    const playerPos = this.level.player.getPosition();
    this.targetPosition.set(playerPos.x, playerPos.y + this.level.config.camera.heightOffset, playerPos.z);

    // Apply orthographic camera offsets if enabled
    if (this.level.config.camera.orthographic) {
      const verticalOffset = this.level.config.camera.orthographicVerticalOffset || 0;
      const horizontalOffset = this.level.config.camera.orthographicHorizontalOffset || 0;

      if ((verticalOffset !== 0 || horizontalOffset !== 0) && this.level.pathPoints.length > 1) {
        // Find nearest path point to get movement direction
        let nearestIndex = this.lastNearestIndex;

        // Get path tangent (movement direction)
        const prevPoint = this.level.pathPoints[Math.max(0, nearestIndex - 1)];
        const nextPoint = this.level.pathPoints[Math.min(this.level.pathPoints.length - 1, nearestIndex + 1)];
        const tangent = nextPoint.subtract(prevPoint).normalize();

        // Calculate perpendicular vector (right direction)
        const right = new Vector3(-tangent.z, 0, tangent.x);

        // Apply offsets: vertical offset perpendicular to path, horizontal offset along path
        this.targetPosition.addInPlace(right.scale(verticalOffset));
        this.targetPosition.addInPlace(tangent.scale(horizontalOffset));
      }
    }

    // Lerp camera target to player position
    // XZ uses the configured smoothing factor for a nice, fluid follow
    const lerpFactor = Math.min(1, this.level.config.camera.smoothing * deltaTime);

    // Y uses a much faster lerp when the player is moving quickly upward,
    // so the camera not always looks up at the player from below after a catapult/launch.
    const verticalDelta = this.targetPosition.y - this.camera.target.y;
    const verticalSpeed = Math.abs(verticalDelta) / (deltaTime || 0.016);
    const verticalLerpBoost = verticalSpeed > 5 ? Math.min(verticalSpeed / 5, 20) : 1;
    const yLerpFactor = Math.min(1, this.level.config.camera.smoothing * deltaTime * verticalLerpBoost);

    const newTarget = Vector3.Lerp(this.camera.target, this.targetPosition, lerpFactor);
    newTarget.y = this.camera.target.y + verticalDelta * yLerpFactor;
    this.camera.target = newTarget;

    // Apply controller right stick camera rotation
    this.applyControllerCameraInput(deltaTime);

    // Align camera to path if enabled
    if (this.level.config.camera.alignToPath && this.level.pathPoints.length > 1) {
      this.alignCameraToPath(deltaTime, playerPos);
    }
  }

  private applyControllerCameraInput(deltaTime: number): void {
    if (this.level.config.camera.orthographic) {
      return;
    }

    const inputState = this.level.inputManager.state;

    // Only apply if there's controller camera input
    if (inputState.cameraX === 0 && inputState.cameraY === 0) {
      return;
    }

    // Apply horizontal rotation (alpha)
    this.camera.alpha -= inputState.cameraX * this.CAMERA_STICK_SENSITIVITY * deltaTime;

    // Apply vertical rotation (beta) - inverted so up on stick looks up
    this.camera.beta -= inputState.cameraY * this.CAMERA_STICK_SENSITIVITY * deltaTime;

    // Clamp beta to prevent camera flipping
    const minBeta = 0.1; // Near top
    const maxBeta = Math.PI - 0.1; // Near bottom
    this.camera.beta = Math.max(minBeta, Math.min(maxBeta, this.camera.beta));
  }

  private alignCameraToPath(deltaTime: number, playerPos: Vector3) {
    const pathPoints = this.level.pathPoints;

    // Find the nearest path point, but only search forward from last position
    // This prevents camera flip when player moves slightly backward
    let nearestIndex = this.lastNearestIndex;
    let nearestDist = Vector3.DistanceSquared(playerPos, pathPoints[nearestIndex] ?? pathPoints[0]);

    // Search a small window around current position (prefer forward)
    const searchStart = Math.max(0, this.lastNearestIndex - 2);
    const searchEnd = Math.min(pathPoints.length - 1, this.lastNearestIndex + 10);

    for (let i = searchStart; i <= searchEnd; i++) {
      const dist = Vector3.DistanceSquared(playerPos, pathPoints[i]);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestIndex = i;
      }
    }

    // Only update if we've moved forward or found a clearly closer point
    if (nearestIndex >= this.lastNearestIndex || nearestDist < 10) {
      this.lastNearestIndex = nearestIndex;
    }

    // Get path tangent at this point (direction player should be moving)
    const prevPoint = pathPoints[Math.max(0, this.lastNearestIndex - 1)];
    const nextPoint = pathPoints[Math.min(pathPoints.length - 1, this.lastNearestIndex + 1)];
    const tangent = nextPoint.subtract(prevPoint).normalize();

    // Calculate target alpha angle from tangent
    let baseAlpha: number;

    if (this.level.config.camera.orthographic) {
      // For orthographic mode, use path tangent + orthographic offset (isometric view)
      // Subtract PI/2 to look along player's movement direction from the correct side
      const pathAngle = Math.atan2(tangent.z, tangent.x);
      baseAlpha = pathAngle - Math.PI / 2 + (this.level.config.camera.orthographicAlpha || Math.PI / 10);
    } else {
      // For perspective mode, camera behind player: atan2(z, x) + PI
      baseAlpha = Math.atan2(tangent.z, tangent.x) + Math.PI;
    }

    this.targetAlpha = baseAlpha;

    // Smoothly rotate camera to target alpha
    const alphaLerpFactor = Math.min(1, this.level.config.camera.alphaSmoothing * deltaTime);

    // Handle angle wrapping for smooth interpolation
    let alphaDiff = this.targetAlpha - this.camera.alpha;
    while (alphaDiff > Math.PI) alphaDiff -= 2 * Math.PI;
    while (alphaDiff < -Math.PI) alphaDiff += 2 * Math.PI;

    this.camera.alpha += alphaDiff * alphaLerpFactor;
  }

  /**
   * Setup orthographic camera mode with isometric-like view from slightly left-above
   * Uses fixed alpha/beta angles for consistent viewing direction
   */
  private setupOrthographicCamera(): void {
    // Switch to orthographic projection
    this.camera.mode = Camera.ORTHOGRAPHIC_CAMERA;

    // Get aspect ratio from canvas
    const aspectRatio = this.canvas.width / this.canvas.height;
    const zoom = this.level.config.camera.orthographicZoom || 10;

    // Set orthographic bounds (symmetric around center)
    this.camera.orthoLeft = -zoom * aspectRatio;
    this.camera.orthoRight = zoom * aspectRatio;
    this.camera.orthoTop = zoom;
    this.camera.orthoBottom = -zoom;

    // Orthographic cameras need explicit far/near plane settings
    this.camera.maxZ = 100000;
    this.camera.minZ = -5000.1;

    // Set viewing angles:
    const orthographicAlpha = this.level.config.camera.orthographicAlpha || Math.PI / 10;
    const orthographicBeta = this.level.config.camera.orthographicBeta || Math.PI / 3;

    // If alignToPath is enabled, combine the orthographic alpha offset with the path direction
    if (this.level.config.camera.alignToPath && this.level.pathPoints.length > 1) {
      const startTangent = this.level.pathPoints[1].subtract(this.level.pathPoints[0]).normalize();
      // Use path angle directly (no +PI) for isometric view relative to path
      const pathAngle = Math.atan2(startTangent.z, startTangent.x);
      this.camera.alpha = pathAngle + orthographicAlpha;
      this.targetAlpha = this.camera.alpha;
    } else {
      this.camera.alpha = orthographicAlpha;
      this.targetAlpha = this.camera.alpha;
      // Lock alpha only if not aligning to path
      this.camera.lowerAlphaLimit = this.camera.alpha;
      this.camera.upperAlphaLimit = this.camera.alpha;
    }

    this.camera.beta = orthographicBeta;

    // Lock beta for consistent vertical view
    this.camera.lowerBetaLimit = this.camera.beta;
    this.camera.upperBetaLimit = this.camera.beta;

    // Disable user input for rotation (isometric should stay fixed)
    this.camera.inputs.clear();
  }

  /**
   * Reset camera to follow player at start position
   * Called when player respawns after death
   */
  resetToPlayer() {
    if (this.level.game.config.sceneViewer) {
      return;
    }

    // Use player's current path index if available, otherwise 0
    // If player is not initialized yet, use config start index
    let index = 0;
    if (this.level.player) {
      index = this.level.player.currentPathIndex;
    } else {
      index = this.level.config.player.startPathIndex;
    }

    this.lookAtPathIndex(index);
  }

  private lookAtPathIndex(index: number): {
    alpha: number;
    position: Vector3;
  } {
    if (this.level.game.config.sceneViewer) {
      return;
    }

    // Reset path tracking
    this.lastNearestIndex = index;

    // Calculate initial alpha from path direction if alignment is enabled
    let initialAlpha = -Math.PI / 2;
    // Try to look forward
    if (this.level.pathPoints.length > index + 1) {
      const p1 = this.level.pathPoints[index];
      const p2 = this.level.pathPoints[Math.min(index + 5, this.level.pathPoints.length - 1)];
      const startTangent = p2.subtract(p1).normalize();
      // Camera behind player: swap arguments for correct ArcRotateCamera coordinate system
      initialAlpha = Math.atan2(startTangent.z, startTangent.x) + Math.PI;
    } else if (index > 0 && this.level.pathPoints.length > index) {
      // Look backward if at end
      const p1 = this.level.pathPoints[index - 1];
      const p2 = this.level.pathPoints[index];
      const startTangent = p2.subtract(p1).normalize();
      initialAlpha = Math.atan2(startTangent.z, startTangent.x) + Math.PI;
    }
    this.targetAlpha = initialAlpha;

    let startPos = this.level.config.path.start;
    if (this.level.pathPoints.length > index) {
      startPos = this.level.pathPoints[index];
    }

    this.targetPosition.set(startPos.x, startPos.y + this.level.config.camera.heightOffset, startPos.z);

    if (this.camera) {
      this.camera.target = this.targetPosition.clone();
      this.camera.alpha = this.targetAlpha;
      this.camera.beta = this.DEFAULT_BETA;
    }

    return {
      alpha: initialAlpha,
      position: startPos.add(new Vector3(0, this.level.config.camera.heightOffset, 0)),
    };
  }
  /**
   * Setup gamepad for free camera movement and rotation
   */
  private setupFreeCameraGamepad(): void {
    this.level.game.setupGamepad((gamepad) => {
      this.freeCameraGamepad = gamepad;
      if (gamepad instanceof Xbox360Pad) {
        console.log('Free camera: Xbox360Pad connected');
      } else if (gamepad instanceof GenericPad) {
        console.log(`Free camera: GenericPad connected - ${gamepad.id}`);
      }
    });
  }

  /**
   * Update free camera with gamepad input
   */
  private updateFreeCamera(deltaTime: number): void {
    if (!this.freeCamera) return;

    const deadzone = 0.15;
    let moveX = 0;
    let moveZ = 0;
    let moveY = 0;
    let rotX = 0;
    let rotY = 0;

    // Gamepad input
    if (this.freeCameraGamepad) {
      const leftStick = this.freeCameraGamepad.leftStick;
      if (leftStick) {
        if (Math.abs(leftStick.x) > deadzone) {
          moveX = leftStick.x; // Strafe left/right
        }
        if (Math.abs(leftStick.y) > deadzone) {
          moveZ = -leftStick.y; // Forward/backward (inverted)
        }
      }

      const rightStick = this.freeCameraGamepad.rightStick;
      if (rightStick) {
        if (Math.abs(rightStick.x) > deadzone) {
          rotY = rightStick.x; // Rotate left/right (yaw)
        }
        if (Math.abs(rightStick.y) > deadzone) {
          rotX = -rightStick.y; // Look up/down (pitch, inverted)
        }
      }
    }

    // Apply rotation
    if (rotX !== 0 || rotY !== 0) {
      // Apply yaw (Y-axis rotation)
      this.freeCamera.rotation.y += rotY * this.FREE_CAMERA_ROTATION_SPEED * deltaTime;

      // Apply pitch (X-axis rotation), clamped to prevent flipping
      const newPitch = this.freeCamera.rotation.x + rotX * this.FREE_CAMERA_ROTATION_SPEED * deltaTime;
      this.freeCamera.rotation.x = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, newPitch));
    }

    // Apply movement relative to camera direction
    if (moveX !== 0 || moveZ !== 0 || moveY !== 0) {
      // Get camera forward and right vectors
      const forward = this.freeCamera.getDirection(Vector3.Forward());
      const right = this.freeCamera.getDirection(Vector3.Right());

      // Compute movement vector
      const movement = new Vector3(0, 0, 0);
      movement.addInPlace(forward.scale(moveZ * this.FREE_CAMERA_GAMEPAD_SPEED * deltaTime));
      movement.addInPlace(right.scale(moveX * this.FREE_CAMERA_GAMEPAD_SPEED * deltaTime));
      movement.y += moveY * this.FREE_CAMERA_GAMEPAD_SPEED * deltaTime;

      // Apply movement
      this.freeCamera.position.addInPlace(movement);
    }
  }

  dispose() {
    if (this.camera) {
      this.camera.dispose();
    }
    if (this.freeCamera) {
      this.freeCamera.dispose();
    }
    this.freeCameraGamepad = null;
  }
}
